// IE Hub - Benefits Service (InBest adapter)
// Starts a benefits entitlement check and returns iframe URL + computed flags

import { httpPost } from "../http";
import { benefitsEndpoints } from "../endpoints";
import { femaCapture } from "../../fema/capture";
import type { ApiResponse, RequestConfig } from "../types";
import { resolveCustomerId } from "../../lib/customerIdResolver";
import { BudgetService } from "./budgetService";

export interface BenefitsCheckData {
  iFrameURL?: string;
  iframeURL?: string;
  extraMonthlyBenefitsAmount?: number;
  waterSupportEntitlement?: "Y" | "N";
  waterSureEntitlement?: "Y" | "N";
  providerName?: string | null;
  waterCompanyExists?: boolean;
}

export class BenefitsService {
  // In-flight request deduplication to prevent duplicate API calls
  // When multiple components call startCheck() within milliseconds,
  // only one network request is made and all callers receive the same response
  private static inFlightRequest: Promise<ApiResponse<BenefitsCheckData>> | null = null;
  private static inFlightCustomerId: number | null = null;

  /**
   * Start a benefits check via legacy InBest endpoint
   * Includes request deduplication - concurrent calls for the same customer
   * will share a single network request
   */
  static async startCheck(
    customerId?: number | null,
    config?: RequestConfig
  ): Promise<ApiResponse<BenefitsCheckData>> {
    const ensuredCustomerId = customerId ?? (await resolveCustomerId());

    if (!ensuredCustomerId) {
      return {
        success: false,
        error: {
          code: "BENEFITS_CHECK_FAILED",
          message: "Customer ID not found",
        },
        timestamp: new Date().toISOString(),
      } as ApiResponse<BenefitsCheckData>;
    }

    // If there's already an in-flight request for the same customer, return it
    // This prevents duplicate network calls when multiple components mount simultaneously
    if (this.inFlightRequest && this.inFlightCustomerId === ensuredCustomerId) {
      return this.inFlightRequest;
    }

    // Create the request and cache it
    this.inFlightCustomerId = ensuredCustomerId;
    this.inFlightRequest = this.executeStartCheck(ensuredCustomerId, config)
      .finally(() => {
        // Clear the cache once the request completes (success or failure)
        this.inFlightRequest = null;
        this.inFlightCustomerId = null;
      });

    return this.inFlightRequest;
  }

  /**
   * Internal method that performs the actual API call
   * Separated from startCheck to enable request deduplication
   */
  private static async executeStartCheck(
    customerId: number,
    config?: RequestConfig
  ): Promise<ApiResponse<BenefitsCheckData>> {
    try {
      const payload = await this.buildInbestPayload(customerId);

      // Call modern InBest endpoint with JWT auth
      const resp = await httpPost<any>(
        benefitsEndpoints.calculateInbest(),
        payload, // Send payload directly (no envelope)
        { 
          headers: config?.headers, 
          timeout: config?.timeout
        }
      );

      // Extract data from response
      const responseData = resp?.data || {};
      
      // Support both iFrameURL (legacy) and iframeURL (modern) casings
      const iframeUrl = responseData.iframeURL || responseData.iFrameURL || '';
      
      return {
        success: true,
        data: {
          iFrameURL: iframeUrl,
          iframeURL: iframeUrl, // Support both casings
          extraMonthlyBenefitsAmount: responseData.extraMonthlyBenefitsAmount,
          waterSupportEntitlement: responseData.waterSupportEntitlement ? 'Y' : 'N',
          waterSureEntitlement: responseData.waterSureEntitlement ? 'Y' : 'N',
          providerName: responseData.providerName,
          waterCompanyExists: responseData.waterCompanyExists
        } as BenefitsCheckData,
        timestamp: new Date().toISOString()
      } as ApiResponse<BenefitsCheckData>;
    } catch (error: any) {
      if (!config?.skipErrorCapture) {
        femaCapture("benefits_start_check_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
      }

      return {
        success: false,
        error: {
          code: "BENEFITS_CHECK_FAILED",
          message: error?.message || "Failed to start benefits check",
        },
        timestamp: new Date().toISOString(),
      } as ApiResponse<BenefitsCheckData>;
    }
  }

  /**
   * Build payload for InBest adapter from local snapshot + budget
   * Mirrors legacy mapping at a minimum; missing fields default to 0 or are omitted.
   */
  private static async buildInbestPayload(customerId: number): Promise<Record<string, any>> {
    const customerData = this.safeParse(localStorage.getItem("customerData"));
    const userData = this.safeParse(localStorage.getItem("userData"));

    // Relationship + partner
    const jointAccount = this.readString(customerData?.jointAccount);
    const partnerDobStr = this.readDateString(
      customerData?.debtor2DateOfBirthStr ?? customerData?.debtor2DateOfBirth
    );
    const userDobStr = this.readDateString(
      customerData?.debtor1DateOfBirthStr ?? customerData?.debtor1DateOfBirth ?? userData?.dob
    );

    const relationshipStatus = partnerDobStr || jointAccount === "Y" ? "couple" : "single";
    const userGender = this.deriveGender(customerData?.customerTitle, customerData?.gender);
    const partnerGender = partnerDobStr ? (userGender === "male" ? "female" : "male") : undefined;

    // Household composition
    const childrenUnder16 = this.toNumber(customerData?.dependentChildrenUnder16);
    const children16Plus = this.toNumber(
      customerData?.dependentChildrenAged16Plus ?? customerData?.dependentChildrenaged16plus
    );
    const adultsIncludingSelf = this.toNumber(
      customerData?.adultsOver18IncludingMyself ?? customerData?.adultsover18includingmyself
    );

    const childrenNum = (childrenUnder16 || 0) + (children16Plus || 0);
    const otherAdultsNum = Math.max(0, (adultsIncludingSelf || 1) - 1);

    // Postcode normalization
    const rawPostcode = this.readString(customerData?.postCode ?? customerData?.postcode);
    const postcode = rawPostcode ? rawPostcode.replace(/\s+/g, "").toUpperCase() : undefined;

    // Income fields for InBest API (only fields expected by backend)
    let userNetSalary = 0;
    let incomeJSA = 0;
    let incomeESA = 0;
    let statutorySickPay = 0;
    let disabilityLivingAllowance = 0;
    let partnerNetBasicStatePension = 0;
    let partnerNetPrivatePension = 0;
    let spousalMaintenancePayments = 0;
    let userPensionContributions = 0;
    let partnerPensionContributions = 0;

    try {
      const incomeResp = await BudgetService.getIncomeData(customerId, { skipErrorCapture: true });
      const record = Array.isArray(incomeResp?.data) ? incomeResp.data[0] : undefined;
      if (record) {
        // Normalize incomes to monthly using only fields expected by backend
        userNetSalary = this.normalizeToMonthly(
          [record.basicIncome || 0],
          [record.basicIncomeMode]
        );
        incomeJSA = this.normalizeToMonthly(
          [record.jobseekerAllowanceContribute || 0],
          [record.jobseekerAllowanceContributeMode]
        );
        incomeESA = this.normalizeToMonthly(
          [record.empSupportAllowanceStatutory || 0],
          [record.empSupportAllowanceStatutoryMode]
        );
        
        // Additional income fields (defaulting to 0 for now)
        statutorySickPay = 0; // Not in current income model
        disabilityLivingAllowance = this.normalizeToMonthly(
          [record.dlaPipAttendanceAllowance || 0],
          [record.dalPipAttendanceAllowanceMode]
        );
        partnerNetBasicStatePension = this.normalizeToMonthly(
          [record.debtor2BasicIncome || 0], // Partner's basic income as proxy
          [record.debtor2BasicIncomeMode]
        );
        partnerNetPrivatePension = this.normalizeToMonthly(
          [record.privatePension || 0],
          [record.privatePensionMode]
        );
        spousalMaintenancePayments = 0; // Not in current income model
        userPensionContributions = 0; // Not in current income model
        partnerPensionContributions = 0; // Not in current income model
      }
    } catch {
      // Non-blocking
    }

    // Always include all fields, even if empty/null
    return {
      customerId,
      referenceNumber: this.readString(userData?.userId) || undefined,
      subpartner: null,
      relationshipStatus,
      childrenNum,
      otherAdultsNum,
      userDob: userDobStr,
      userGender,
      partnerDob: partnerDobStr || "", // Always include (empty string if not provided)
      partnerGender: partnerGender || "male", // Always include (default to "male" if not provided)
      type: 1,
      postcode,
      incomeJSA,
      incomeESA,
      statutorySickPay,
      disabilityLivingAllowance,
      partnerNetBasicStatePension,
      partnerNetPrivatePension,
      spousalMaintenancePayments,
      userPensionContributions,
      partnerPensionContributions,
      userNetSalary,
    };
  }

  // --- helpers ---
  private static safeParse(raw: string | null): any | null {
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  private static readString(value: unknown): string | undefined {
    if (typeof value === "string" && value.trim().length > 0) return value.trim();
    if (typeof value === "number" && !isNaN(value)) return String(value);
    return undefined;
  }

  private static toNumber(value: unknown): number | undefined {
    if (typeof value === "number" && !isNaN(value)) return value;
    if (typeof value === "string" && value.trim().length > 0 && !isNaN(Number(value))) {
      return Number(value);
    }
    return undefined;
  }

  private static readDateString(value: unknown): string | undefined {
    if (!value) return undefined;
    if (typeof value === "string" && /\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
    if (value instanceof Date && !isNaN(value.getTime())) {
      const y = value.getFullYear();
      const m = String(value.getMonth() + 1).padStart(2, "0");
      const d = String(value.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    }
    return undefined;
  }

  private static deriveGender(title?: unknown, fallback?: unknown): "male" | "female" {
    const t = typeof title === "string" ? title.toLowerCase() : "";
    if (t === "mr") return "male";
    if (t === "mrs" || t === "miss" || t === "ms") return "female";
    const f = typeof fallback === "string" ? fallback.toLowerCase() : "";
    if (f === "male" || f === "female") return f as any;
    return "male";
  }

  private static normalizeToMonthly(values: number[], modes?: (string | undefined)[]): number {
    if (!values || values.length === 0) return 0;
    let total = 0;
    for (let i = 0; i < values.length; i++) {
      const value = values[i] || 0;
      const mode = (modes?.[i] || "M").toUpperCase();
      switch (mode) {
        case "2W":
          total += (value * 26) / 12; // bi-weekly → monthly
          break;
        case "4W":
          total += value; // 4-week assumed monthly in legacy
          break;
        case "Q":
          total += value / 3; // quarterly → monthly
          break;
        case "Y":
          total += value / 12; // yearly → monthly
          break;
        case "M":
        default:
          total += value;
          break;
      }
    }
    return total;
  }
}

export default BenefitsService;


